import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type ModelConfiguratorSpecsFragment } from '../../../../api/fragments/ModelConfiguratorSpecs';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteConfiguratorDescriptionImageDocument,
    type DeleteConfiguratorDescriptionImageMutation,
    type DeleteConfiguratorDescriptionImageMutationVariables,
} from '../../../../api/mutations/deleteConfiguratorDescriptionImage';
import {
    DeleteModelConfiguratorAssetDocument,
    type DeleteModelConfiguratorAssetMutation,
    type DeleteModelConfiguratorAssetMutationVariables,
} from '../../../../api/mutations/deleteModelConfiguratorAsset';
import {
    UploadConfiguratorDescriptionImageDocument,
    type UploadConfiguratorDescriptionImageMutation,
    type UploadConfiguratorDescriptionImageMutationVariables,
} from '../../../../api/mutations/uploadConfiguratorDescriptionImage';
import {
    UploadModelConfiguratorAssetDocument,
    type UploadModelConfiguratorAssetMutation,
    type UploadModelConfiguratorAssetMutationVariables,
} from '../../../../api/mutations/uploadModelConfiguratorAsset';
import { ModelConfiguratorAsset, type UploadedFileWithPreview } from '../../../../api/types';
import { type ModelConfiguratorFormValues } from './utils';

const useHandleModelConfiguratorAssets = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        (id: string, values: ModelConfiguratorFormValues, initialValues?: ModelConfiguratorSpecsFragment) => {
            const { banner, mobileBanner, descriptionImages } = values;
            const uploads = [];

            if (banner instanceof File) {
                uploads.push(
                    apolloClient.mutate<
                        UploadModelConfiguratorAssetMutation,
                        UploadModelConfiguratorAssetMutationVariables
                    >({
                        mutation: UploadModelConfiguratorAssetDocument,
                        variables: { id, type: ModelConfiguratorAsset.Banner, upload: banner },
                    })
                );
            } else if (banner === null && isObject(initialValues.banner)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<
                        DeleteModelConfiguratorAssetMutation,
                        DeleteModelConfiguratorAssetMutationVariables
                    >({
                        mutation: DeleteModelConfiguratorAssetDocument,
                        variables: { id, type: ModelConfiguratorAsset.Banner },
                    })
                );
            }

            if (mobileBanner instanceof File) {
                uploads.push(
                    apolloClient.mutate<
                        UploadModelConfiguratorAssetMutation,
                        UploadModelConfiguratorAssetMutationVariables
                    >({
                        mutation: UploadModelConfiguratorAssetDocument,
                        variables: { id, type: ModelConfiguratorAsset.MobileBanner, upload: mobileBanner },
                    })
                );
            } else if (banner === null && isObject(initialValues.banner)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<
                        DeleteModelConfiguratorAssetMutation,
                        DeleteModelConfiguratorAssetMutationVariables
                    >({
                        mutation: DeleteModelConfiguratorAssetDocument,
                        variables: { id, type: ModelConfiguratorAsset.MobileBanner },
                    })
                );
            }

            const descriptionImagesToUploadHaveIds = descriptionImages.filter(
                (image: File | UploadFileWithPreviewFormDataFragment) => !(image instanceof File)
            ) as UploadedFileWithPreview[];

            const imageIdsToDelete = [];

            initialValues?.descriptionImages?.forEach(image => {
                if (
                    image.id &&
                    !descriptionImagesToUploadHaveIds.find(imageToUpload => imageToUpload?.id === image.id)
                ) {
                    imageIdsToDelete.push(image.id);
                }
            });

            for (const image of descriptionImages) {
                if (image instanceof File) {
                    uploads.push(
                        apolloClient.mutate<
                            UploadConfiguratorDescriptionImageMutation,
                            UploadConfiguratorDescriptionImageMutationVariables
                        >({
                            mutation: UploadConfiguratorDescriptionImageDocument,
                            variables: { modelConfiguratorId: id, upload: image },
                        })
                    );
                }
            }

            imageIdsToDelete.forEach(imageId => {
                uploads.push(
                    apolloClient.mutate<
                        DeleteConfiguratorDescriptionImageMutation,
                        DeleteConfiguratorDescriptionImageMutationVariables
                    >({
                        mutation: DeleteConfiguratorDescriptionImageDocument,
                        variables: { modelConfiguratorId: id, imageId },
                    })
                );
            });

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [apolloClient]
    );
};

export default useHandleModelConfiguratorAssets;
